/* Phase 10.8A-4 · Ordo AI™ Proof Engine
 *
 * The diocesan demonstration surface. Not just an admin tool — this
 * is the sales artefact: "here is exactly why Ordo AI™ recommended
 * this musical choice, what sources support it, what rules were
 * satisfied, and what pastoral factors influenced the recommendation."
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminMusicalIntelligenceCanary.css";
import "./AdminOrdoAiProofEngine.css";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/intelligence`;

const VERDICT_STYLE = {
  PASS:   { bg: "#0e7a2c", label: "PASS" },
  WARN:   { bg: "#8a5a00", label: "WARN" },
  REJECT: { bg: "#a63232", label: "REJECT" },
};

const AUTHORITY_LABEL = {
  sacred_tradition:     "1 · Sacred Tradition",
  liturgical_law:       "2 · Liturgical Law",
  conference_directive: "3 · Conference Directive",
  diocesan_norm:        "4 · Diocesan Norm",
  parish_custom:        "5 · Parish Custom",
  pastoral_preference:  "6 · Pastoral Preference",
  ai_optimization:      "7 · AI Optimization",
};

async function fx(path, init = {}) {
  const res = await fetch(`${BASE}${path}`, { credentials: "include", ...init });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json();
}

export default function AdminOrdoAiProofEngine() {
  const [tab, setTab] = useState("demo");
  const [bundle, setBundle] = useState(null);
  const [rules, setRules] = useState([]);
  const [demo, setDemo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ season: "", action: "", layer: "" });

  const loadBundle = useCallback(async () => {
    try {
      const b = await fx(`/bundles/catholic_roman_rite`);
      setBundle(b.bundle);
      setRules(b.rules || []);
    } catch (e) { setError(String(e.message)); }
  }, []);

  const runDemo = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const d = await fx(`/demo/advent-4`, { method: "POST" });
      setDemo(d);
    } catch (e) { setError(String(e.message)); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { loadBundle(); }, [loadBundle]);
  useEffect(() => { if (tab === "demo" && !demo) runDemo(); }, [tab, demo, runDemo]);

  const filteredRules = useMemo(() => rules.filter((r) => {
    if (filter.season && !(r.seasons || []).includes(filter.season)
        && r.seasons?.length > 0) return false;
    if (filter.action && !(r.liturgical_actions || []).includes(filter.action)
        && r.liturgical_actions?.length > 0) return false;
    if (filter.layer && r.layer !== filter.layer) return false;
    return true;
  }), [rules, filter]);

  return (
    <div className="mi-canary ordo-proof" data-testid="ordo-ai-proof-engine">
      <header className="mi-canary-header ordo-header">
        <div>
          <h1 data-testid="proof-engine-title">Ordo AI™ · Proof Engine</h1>
          <p className="mi-canary-sub">
            The reasoning behind every Musical Intelligence™ recommendation —
            deterministic rubrics, source citations, and the pastoral
            factors that shaped the choice.
          </p>
          {bundle && (
            <p style={{ fontSize: 12, color: "#666" }} data-testid="bundle-info">
              Rule bundle: <code>{bundle.id}</code> v{bundle.version} · {rules.length} rules loaded
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="mi-run" onClick={runDemo} disabled={busy}
                  data-testid="run-demo-btn">
            {busy ? "Running…" : "Run diocesan demo (Advent 4)"}
          </button>
        </div>
      </header>

      {error && <div className="mi-canary-error" data-testid="proof-error">{error}</div>}

      <section className="mi-canary-panel ordo-tabs" data-testid="proof-tabs"
               style={{ display: "flex", gap: 8, padding: 6 }}>
        {[
          { id: "demo",   label: "Diocesan Demo (Advent 4)" },
          { id: "rules",  label: "Rule Catalogue" },
          { id: "framework", label: "Reasoning Framework" },
        ].map((t) => (
          <button key={t.id} className="mi-run"
                  data-testid={`proof-tab-${t.id}`}
                  style={{
                    background: tab === t.id ? "#c9963a" : "#22343f",
                    padding: "6px 14px", fontSize: 13,
                  }}
                  onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </section>

      {tab === "demo"      && <DemoTab demo={demo} busy={busy} />}
      {tab === "rules"     && <RulesTab rules={filteredRules} filter={filter} setFilter={setFilter} />}
      {tab === "framework" && <FrameworkTab />}
    </div>
  );
}


function DemoTab({ demo, busy }) {
  if (busy || !demo) {
    return <section className="mi-canary-panel" data-testid="demo-loading">
      Loading demonstration…
    </section>;
  }
  return (
    <>
      <section className="mi-canary-panel ordo-context" data-testid="demo-context">
        <h2 style={{ marginTop: 0 }}>Fourth Sunday of Advent · Roman Catholic</h2>
        <p style={{ fontStyle: "italic", color: "#333" }}>{demo.framework_narrative}</p>
      </section>

      {demo.recommendations.map((rec) => {
        const v = rec.validation;
        const rc = rec.reason_chain || {};
        const style = VERDICT_STYLE[v.verdict] || VERDICT_STYLE.PASS;
        return (
          <section key={rec.role}
                   className="mi-canary-panel ordo-recommendation"
                   data-testid={`demo-slot-${rec.role}`}>
            <div className="ordo-slot-header">
              <div>
                <span className="ordo-slot-role">{rec.role.replace(/_/g, " ")}</span>
                <h3 style={{ margin: 0 }}>{rec.work.title}</h3>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {rc.confidence != null && (
                  <span className="ordo-confidence-pill"
                        data-testid={`confidence-${rec.role}`}>
                    {(rc.confidence * 100).toFixed(0)}%
                  </span>
                )}
                <span className="ordo-verdict-pill"
                      style={{ background: style.bg }}
                      data-testid={`verdict-${rec.role}`}>
                  {rc.decision || style.label}
                </span>
              </div>
            </div>

            {rc.reason_chain && rc.reason_chain.length > 0 && (
              <div className="ordo-chain" data-testid={`chain-${rec.role}`}>
                {rc.reason_chain.map((step, i) => (
                  <div key={i} className={`ordo-chain-row ordo-chain-${step.result.toLowerCase()}`}>
                    <span className="ordo-chain-layer">{step.layer.replace(/_/g, " ")}</span>
                    <span className="ordo-chain-result">{step.result}</span>
                    <span className="ordo-chain-source">{step.source}</span>
                  </div>
                ))}
              </div>
            )}

            {rc.narrative && (
              <p className="ordo-narrative" data-testid={`narrative-${rec.role}`}>
                {rc.narrative}
              </p>
            )}

            {v.reasons.length > 0 && (
              <ul className="ordo-reason-list" data-testid={`reasons-${rec.role}`}>
                {v.reasons.map((r, i) => (
                  <li key={r + i}>
                    <code>{r}</code>
                    {v.explanations?.[i] && <span> — {v.explanations[i]}</span>}
                  </li>
                ))}
              </ul>
            )}

            {rc.authority_consulted && rc.authority_consulted.length > 0 && (
              <div className="ordo-authority-chain" data-testid={`authority-${rec.role}`}>
                {rc.authority_consulted.map((lvl) => (
                  <span key={lvl} className="ordo-authority-pill"
                        title={AUTHORITY_LABEL[lvl] || lvl}>
                    {AUTHORITY_LABEL[lvl] || lvl}
                  </span>
                ))}
              </div>
            )}

            {v.rule_ids.length > 0 && (
              <details style={{ marginTop: 8, fontSize: 12 }}>
                <summary>Rules consulted</summary>
                <ul>
                  {v.rule_ids.map((rid) => (<li key={rid}><code>{rid}</code></li>))}
                </ul>
              </details>
            )}
          </section>
        );
      })}
    </>
  );
}


function RulesTab({ rules, filter, setFilter }) {
  return (
    <section className="mi-canary-panel" data-testid="rules-tab">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12,
                     alignItems: "center" }}>
        <select className="mi-canary-input" value={filter.season}
                data-testid="rules-filter-season"
                onChange={(e) => setFilter({ ...filter, season: e.target.value })}>
          <option value="">All seasons</option>
          <option value="advent">Advent</option>
          <option value="lent">Lent</option>
          <option value="easter">Easter</option>
          <option value="easter_triduum">Easter Triduum</option>
        </select>
        <select className="mi-canary-input" value={filter.action}
                data-testid="rules-filter-action"
                onChange={(e) => setFilter({ ...filter, action: e.target.value })}>
          <option value="">All actions</option>
          <option value="entrance">Entrance</option>
          <option value="gloria">Gloria</option>
          <option value="psalm">Psalm</option>
          <option value="gospel_acclamation">Gospel Acclamation</option>
          <option value="offertory">Offertory</option>
          <option value="sanctus">Sanctus</option>
          <option value="communion">Communion</option>
          <option value="closing">Closing</option>
        </select>
        <select className="mi-canary-input" value={filter.layer}
                data-testid="rules-filter-layer"
                onChange={(e) => setFilter({ ...filter, layer: e.target.value })}>
          <option value="">All layers</option>
          <option value="rubric">Layer 1 · Rubric</option>
          <option value="validation">Layer 2 · Validation</option>
          <option value="recommendation">Layer 3 · Recommendation</option>
          <option value="explanation">Layer 4 · Explanation</option>
        </select>
      </div>

      <p style={{ fontSize: 12, color: "#666" }}>{rules.length} rule(s)</p>

      {rules.map((r) => (
        <details key={r.id} className="ordo-rule-card"
                 data-testid={`rule-card-${r.id}`}>
          <summary>
            <span className={`ordo-severity ordo-severity-${r.severity}`}>
              {r.severity}
            </span>
            <strong>{r.title}</strong>
            <span style={{ color: "#666", fontSize: 12 }}>
              &nbsp;· {r.reason_code}
            </span>
          </summary>
          <p>{r.summary}</p>
          <div style={{ fontSize: 12, color: "#333" }}>
            <b>Seasons:</b> {(r.seasons || []).join(", ") || "any"}
            &nbsp;&nbsp;<b>Actions:</b> {(r.liturgical_actions || []).join(", ") || "any"}
            &nbsp;&nbsp;<b>Layer:</b> {r.layer}
          </div>
          {r.references?.length > 0 && (
            <ul className="ordo-references">
              {r.references.map((ref) => (
                <li key={ref.code}>
                  <b>{ref.code}</b> · {ref.document}
                  {ref.section && ` · §${ref.section}`}
                  <span className="ordo-authority-pill" style={{ marginLeft: 6 }}>
                    {AUTHORITY_LABEL[ref.authority_level] || ref.authority_level}
                  </span>
                  {ref.text_excerpt && (
                    <blockquote style={{ margin: "6px 0", fontSize: 12,
                                          color: "#555", fontStyle: "italic" }}>
                      "{ref.text_excerpt}"
                    </blockquote>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div style={{ fontSize: 11, color: "#999" }}>Rule id: <code>{r.id}</code></div>
        </details>
      ))}
    </section>
  );
}


function FrameworkTab() {
  return (
    <section className="mi-canary-panel ordo-framework" data-testid="framework-tab">
      <h2 style={{ marginTop: 0 }}>Ordo AI™ Reasoning Framework</h2>
      <p><em>Version 1.0.0 · Rasaan Hakiem LLC · 2026-07-28.</em></p>

      <h3>The four layers</h3>
      <ol className="ordo-layer-list">
        <li><b>Rubrics.</b> Canonical sources of authority — GIRM, Sacrosanctum Concilium, Musicam Sacram — compiled into a versioned rule catalogue with immutable citations.</li>
        <li><b>Validation.</b> Deterministic evaluation of a candidate work against every applicable rubric. Emits PASS / WARN / REJECT.</li>
        <li><b>Recommendations.</b> Ranks a shortlist consuming validation output plus parish profile, season, and editorial evaluations.</li>
        <li><b>Explanations.</b> Human- and auditor-friendly rationale. Deterministic templates by default; AI narrative opt-in per tier.</li>
      </ol>

      <h3>Source authority hierarchy</h3>
      <ol className="ordo-authority-list">
        <li>Sacred Tradition · Official Church Documents</li>
        <li>Liturgical Law (GIRM, BCP, denominational rubrics)</li>
        <li>Conference Directives (USCCB, CCCB, General Synod)</li>
        <li>Diocesan Norms</li>
        <li>Parish Customs</li>
        <li>Pastoral Preference</li>
        <li>AI Optimization</li>
      </ol>
      <p style={{ fontSize: 13, color: "#333" }}>
        Ordo AI™ can operate freely at Level 7 within the space left legal by
        every level above. It cannot invoke Level 7 to overrule any of
        Levels 1–6.
      </p>

      <h3>What Ordo AI™ cannot do</h3>
      <ul>
        <li>Override any mandatory rubric. Mandatory violations always REJECT.</li>
        <li>Author or invent liturgical text (prayers, formulas, scripture translations).</li>
        <li>Modify canonical works or tradition-work usage without human approval.</li>
        <li>Introduce a rule that lacks at least one authoritative citation.</li>
      </ul>
    </section>
  );
}
